import { type GQLSubGroupDetailSparePartFragment } from "@src/graphql.generated";
import { z } from "zod";

export enum Events {
    SPFAddToCart = "spfAddToCart",
    SPFAddToCartProcessed = "spfAddToCartProcessed",
    SPFAddToWishlist = "spfAddToWishlist",
    /* consumed */
    SPFArticleInfos = "spfArticleInfos",
    SPFOnPowerPartsButtonClick = "spfOnPowerPartsButtonClick",
    SPFOnSparePartKitsButtonClick = "spfOnSparePartKitsButtonClick",
    /* sent */
    SPFOnWebComponentLoaded = "spfOnWebComponentLoaded",
    SPFRemoveFromWishlist = "spfRemoveFromWishlist",
    SPFRequestArticleInfosForArticles = "spfRequestArticleInfosForArticles",

    SPFRequestWishlistInfo = "spfRequestWishlistInfo",
    SPFSendRequestForArticle = "spfSendRequestForArticle",
    SPFWishlistInfo = "spfWishlistInfo",
}

export enum SPFAvailabilityStatus {
    Available = "available",
    ManufacturerAvailable = "manufacturerAvailable",
    NotAvailable = "notAvailable",
    Unknown = "unknown",
}

export interface SPFArticleInfo {
    articleNumber: string;
    availability: SPFAvailabilityStatus;
    availabilityText?: string;
    clientArticleNumber?: string;
    currency?: string;
    price?: number;
}

export interface SPFArticleWithPriceInfo extends GQLSubGroupDetailSparePartFragment {
    clientArticleInfo?: SPFArticleInfo;
}

export interface SPFArticleInfosEvent {
    detail: {
        infos: SPFArticleInfo[];
    };
    type: Events.SPFArticleInfos;
}

export interface SPFAddToCartProcessedEvent {
    detail: {
        errorArticleNumbers?: string[];
        successfulArticleNumbers: string[];
    };
    type: Events.SPFAddToCartProcessed;
}

export const SPFWishlistInfoDetailSchema = z.object({
    detail: z.object({
        wishlistArticleNumbers: z.string().array(),
    }),
});
export type SPFWishlistInfoEventDetail = z.infer<typeof SPFWishlistInfoDetailSchema>;

export interface SPFWishlistInfoEvent {
    detail: SPFWishlistInfoEventDetail;
    type: Events.SPFWishlistInfo;
}

export const dispatchOnWebComponentLoadedEvent = (): void => {
    const loadedEvent = new CustomEvent(Events.SPFOnWebComponentLoaded);
    document.dispatchEvent(loadedEvent);
};

export interface ArticleNumberWithBrand {
    articleNumber: string;
    brand: string;
}

export interface SPFRequestArticleInfosForArticleNumbersEvent {
    detail: {
        items: ArticleNumberWithBrand[];
    };
    type: Events.SPFRequestArticleInfosForArticles;
}

export const dispatchRequestArticleInfosForArticlesEvent = (items: ArticleNumberWithBrand[]): void => {
    const getSparePartDetailsEvent = new CustomEvent<SPFRequestArticleInfosForArticleNumbersEvent>(Events.SPFRequestArticleInfosForArticles, {
        detail: {
            type: Events.SPFRequestArticleInfosForArticles,
            detail: {
                items,
            },
        },
    });
    document.dispatchEvent(getSparePartDetailsEvent);
};

export interface SPFAddToCartEvent {
    detail: {
        articleNumber: string;
        brand: string;
        clientArticleNumber?: string;
        quantity: number;
    };
    type: Events.SPFAddToCart;
}

export const dispatchSPFAddToCartEvent = (articleNumber: string, brand: string, quantity: number, clientArticleNumber?: string): void => {
    const addToCartEvent = new CustomEvent<SPFAddToCartEvent>(Events.SPFAddToCart, {
        detail: {
            type: Events.SPFAddToCart,
            detail: {
                articleNumber,
                brand,
                quantity,
                clientArticleNumber,
            },
        },
    });
    document.dispatchEvent(addToCartEvent);
};

export const dispatchRequestWishlistInfoEvent = (): void => {
    const requestWishlistEvent = new CustomEvent(Events.SPFRequestWishlistInfo);
    document.dispatchEvent(requestWishlistEvent);
};

export interface SPFAddToWishlistEvent {
    detail: {
        articleNumber: string;
        brand: string;
        clientArticleNumber?: string;
    };
    type: Events.SPFAddToWishlist;
}

export const dispatchSPFAddToWishlistEvent = (articleNumber: string, brand: string, clientArticleNumber?: string): void => {
    const addToWishlistEvent = new CustomEvent<SPFAddToWishlistEvent>(Events.SPFAddToWishlist, {
        detail: {
            type: Events.SPFAddToWishlist,
            detail: {
                articleNumber,
                brand,
                clientArticleNumber,
            },
        },
    });
    document.dispatchEvent(addToWishlistEvent);
};

export interface SPFRemoveFromWishlistEvent {
    detail: {
        articleNumber: string;
        brand: string;
        clientArticleNumber?: string;
    };
    type: Events.SPFRemoveFromWishlist;
}

export const dispatchSPFRemoveFromWishlistEvent = (articleNumber: string, brand: string, clientArticleNumber?: string): void => {
    const removeFromWishlistEvent = new CustomEvent<SPFRemoveFromWishlistEvent>(Events.SPFRemoveFromWishlist, {
        detail: {
            type: Events.SPFRemoveFromWishlist,
            detail: {
                articleNumber,
                brand,
                clientArticleNumber,
            },
        },
    });
    document.dispatchEvent(removeFromWishlistEvent);
};

export interface SPFSendRequestForArticleEvent {
    detail: {
        articleNumber: string;
        brand: string;
    };
    type: Events.SPFSendRequestForArticle;
}

export const dispatchSendRequestEvent = (articleNumber: string, brand: string): void => {
    const sendRequestEvent = new CustomEvent<SPFSendRequestForArticleEvent>(Events.SPFSendRequestForArticle, {
        detail: {
            type: Events.SPFSendRequestForArticle,
            detail: {
                articleNumber,
                brand,
            },
        },
    });
    document.dispatchEvent(sendRequestEvent);
};

export interface SPFOnPowerPartsButtonClickEvent {
    detail: {
        articleNumber: string;
    };
    type: Events.SPFOnPowerPartsButtonClick;
}

export const dispatchSPFOnPowerPartsButtonClickEvent = (articleNumber: string): void => {
    const event = new CustomEvent<SPFOnPowerPartsButtonClickEvent>(Events.SPFOnPowerPartsButtonClick, {
        detail: {
            type: Events.SPFOnPowerPartsButtonClick,
            detail: {
                articleNumber,
            },
        },
    });
    document.dispatchEvent(event);
};

export interface SPFOnSparePartKitsButtonClickEvent {
    detail: {
        articleNumber: string;
    };
    type: Events.SPFOnSparePartKitsButtonClick;
}

export const dispatchSPFOnSparePartKitsButtonClickEvent = (articleNumber: string): void => {
    const event = new CustomEvent<SPFOnSparePartKitsButtonClickEvent>(Events.SPFOnSparePartKitsButtonClick, {
        detail: {
            type: Events.SPFOnSparePartKitsButtonClick,
            detail: {
                articleNumber,
            },
        },
    });
    document.dispatchEvent(event);
};
